import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useStreamService } from '@/contexts/StreamServiceContext';
import { sendCommand } from '@/redux/new_services/device';

// FRESH_MS — wenn die letzte beliebige MQTT-Nachricht jünger als dieser
// Zeitraum ist, gilt das Gerät als online, auch wenn nie explizit ein
// `status="Online"` Topic gesehen wurde. Status-Topics werden nicht von
// jedem Gerät retained gepublished; ohne diese Heuristik bleibt das
// Dashboard auf "offline" obwohl Memory/Network/DJ-Updates eintrudeln.
const FRESH_MS = 30_000;

// Apply a topic→payload map (mqtt_snapshot from the API) to the same
// stateful slots the SSE callback would populate. Mirrors the topic
// switch below so existing topics are read in exactly one place. Returns
// the seed values for the four state slots that need a starting point.
const seedFromSnapshot = (snapshot) => {
  const informations = { hostname: '', version: '' };
  const memory = { free: 0, total: 0, used: 0 };
  const dj = { audio_volume: 0, stream_volume: 0, offline_volume: 0, spot_volume: 0, output: '' };
  if (!snapshot || typeof snapshot !== 'object') {
    return { informations, memory, dj };
  }
  Object.entries(snapshot).forEach(([topic, msg]) => {
    if (topic === 'version') informations.version = msg;
    else if (topic === 'hostname') informations.hostname = msg;
    else if (topic.startsWith('data/memory/')) {
      const key = topic.split('/')[2];
      memory[key] = msg;
    } else if (topic.startsWith('data/micast-dj/')) {
      const key = topic.split('/')[2];
      dj[key] = msg;
    }
  });
  return { informations, memory, dj };
};

const useDeviceData = (deviceUuid, initialSnapshot = null) => {
  const { subscribe, unsubscribe, sessionID } = useStreamService();
  const seed = useState(() => seedFromSnapshot(initialSnapshot))[0];
  const [data, setData] = useState([]);
  // The snapshot intentionally never seeds `lastMessage` —
  // it must reflect *current* MQTT activity, otherwise an offline
  // device would show "vor ein paar Sekunden" again.
  const [lastMessage, setLastMessage] = useState();
  // `explicitStatus` is the last value reported via the `status` topic
  // (LWT/Birth), tristate: 'online' | 'offline' | null. We never assume
  // it — the FRESH_MS-window on `lastMessage` covers the (common) case
  // where the device publishes data topics but no retained status.
  const [explicitStatus, setExplicitStatus] = useState(null);
  // Tick state forces re-renders so the derived `online` value (which
  // depends on `now - lastMessage < FRESH_MS`) flips to false on its
  // own when the device falls silent. Without this, the dot would stay
  // green until the user navigates away and back.
  const [now, setNow] = useState(() => Date.now());
  const [memory, setMemory] = useState(seed.memory);
  const [informations, setInformations] = useState(seed.informations);
  const [networks, setNetworks] = useState({});
  const [access_points, setAccessPoints] = useState({});
  const [dj, setDj] = useState(seed.dj);
  const [error, setError] = useState(null);

  const [doScan, setDoScan] = useState(false);

  useEffect(() => {
    if (!deviceUuid) {
      return;
    }
    const callback = (data) => {

      try {
        let o = data;

        setLastMessage(moment());

        if (o.topic) {

          if (o.topic === "version") {
            setInformations(prevInformations => ({...prevInformations, version: o.msg}));
            return;
          }
          if (o.topic === "hostname") {
            setInformations(prevInformations => ({...prevInformations, hostname: o.msg}));
            return;
          }

          if (o.topic === "status") {
            setExplicitStatus(o.msg === "Online" ? 'online' : 'offline');
            return;
          }

          if(o.topic.startsWith("data/memory/")) {
            let memory = o.topic.split("/")[2];
            setMemory(prevMemory => {
              return {
                ...prevMemory,
                [memory]: o.msg
              }
            });
            return;
          }

          if(o.topic.startsWith("data/micast-dj/")) {
            let dj = o.topic.split("/")[2];
            setDj(prevDj => {
              return {
                ...prevDj,
                [dj]: o.msg
              }
            });
            return
          }

          if(o.topic.startsWith("data/network/devices/")) {
            let device_name = o.topic.split("/")[3];
            setNetworks(prevNetworks => {
              return {
                ...prevNetworks,
                [device_name]: JSON.parse(o.msg)
              }
            })
            return;
          }

          if(o.topic.startsWith("data/network/access_points/") && o.msg !== "") {
            let device_name = o.topic.split("/")[3];
            try {
              let m = JSON.parse(o.msg);
              setAccessPoints(prevNetworks => {
                return {
                  ...prevNetworks,
                  [device_name]: m,
                }
              })
            } catch (e) {
              console.error("Error AccessPoint parsing message", e, o);
            }
            return;
          }

          if(o.msg) {
            try {
              o = {
                topic: o.topic,
                msg: JSON.parse(o.msg)
              }
            } catch (e) {
              //console.error("Error parsing message", e);
            }
          }
        }

      } catch (e) {
        console.info("error while parsing data", e);
      }
    }

    subscribe(`devices/${deviceUuid}/#`, callback);

    return () => {
      unsubscribe(`devices/${deviceUuid}/#`);
    }
  }, [deviceUuid, subscribe, unsubscribe]);

  // Tick every 5s while the hook is mounted so the freshness check on
  // `lastMessage` re-evaluates and `online` flips back to false when the
  // device falls silent. Interval is cheap; the dot only re-renders if
  // the derived value actually changes.
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(handle);
  }, []);

  // Derived `online`: an explicit LWT-style "Offline" always wins (the
  // device told us it's gone), an explicit "Online" obviously means
  // online, and otherwise we trust the freshness of the last inbound
  // MQTT message — any topic counts.
  const online = useMemo(() => {
    if (explicitStatus === 'offline') return false;
    if (explicitStatus === 'online') return true;
    if (!lastMessage) return false;
    return now - lastMessage.valueOf() < FRESH_MS;
  }, [explicitStatus, lastMessage, now]);

  const scanWifi = (id) => {
    sendCommand(sessionID, id, "network", {set: "scan", value: ""});
  }

  // Connect a wireless interface to an SSID. Password is optional for open APs.
  const connectWifi = (id, iface, ssid, password = "") => {
    sendCommand(sessionID, id, "network", {
      set: "connect",
      value: JSON.stringify({ interface: iface, ssid, password }),
    });
  }

  // Disconnect / drop the current association on a wireless interface.
  const disconnectWifi = (id, iface) => {
    sendCommand(sessionID, id, "network", {
      set: "disconnect",
      value: JSON.stringify({ interface: iface }),
    });
  }

  // Forget / remove a saved WLAN profile.
  const forgetWifi = (id, iface, ssid) => {
    sendCommand(sessionID, id, "network", {
      set: "forget",
      value: JSON.stringify({ interface: iface, ssid }),
    });
  }

  // Apply IPv4 configuration on the given interface. `config` is
  // { dhcp: bool, addresses: string[], gateway: string, dns: string[] }.
  const applyInterfaceConfig = (id, iface, config) => {
    sendCommand(sessionID, id, "network", {
      set: "config",
      value: JSON.stringify({ interface: iface, ...config }),
    });
  }

  const setVolume = (id, target, value) => {
    sendCommand(sessionID, id, "micast-dj", {set: target, value: String(value)});
  }

  return {
    data,
    online,
    informations,
    memory,
    dj,
    error,
    lastMessage,
    networks,
    access_points,
    scanWifi,
    connectWifi,
    disconnectWifi,
    forgetWifi,
    applyInterfaceConfig,
    setVolume,
  };
};

export default useDeviceData
